import { Component, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { map, take } from 'rxjs/operators';
import {
    ToastButtons,
    ToastMessageType,
    ToastResponse,
    ToastService
} from '../../../../Common/toast';
import { TenantService } from '../../../../Services';
import { AvaModule, ModuleItem, PurgeDataRequest } from '../Models';
import { TranslationService } from '../Services/translation.service';
import { PurgeDataService } from './purge-data.service';
import { AsyncPipe } from '@angular/common';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/autocomplete';
import { MatButton } from '@angular/material/button';
import { MatCard, MatCardTitle, MatCardSubtitle, MatCardContent } from '@angular/material/card';
import { JobLiveViewComponent } from '../../../../Jobs/job.liveview.component';

@Component({
    selector: 'aw-purge-data',
    templateUrl: './purge-data.component.html',
    styleUrls: ['./purge-data.component.less'],
    imports: [MatSelect, MatOption, MatButton, MatCard, MatCardTitle, MatCardSubtitle, MatCardContent, JobLiveViewComponent, AsyncPipe]
})
export class PurgeDataComponent implements OnInit, OnDestroy {
    public moduleItems: Observable<ModuleItem[]>;
    public selectedModules: string[] = [];
    public purging: boolean;

    protected purgingJobId: string;
    private subscriptions: Subscription[] = [];

    constructor(
        private purgeDataService: PurgeDataService,
        private tenantService: TenantService,
        private translationService: TranslationService,
        private toast: ToastService
    ) { }

    public ngOnInit(): void {
        this.moduleItems = this.getModules();

        this.purging = false;
    }

    public isRemoveRecordsButtonAvailable(): boolean {
        return this.selectedModules?.length > 0;
    }

    public transformModule(module: AvaModule): ModuleItem {
        let moduleItem: ModuleItem;
        this.subscriptions.push(
            this.translationService.getTranslation(module.Name, 'Text').subscribe(
                (name) =>
                (moduleItem = {
                    id: module.UniqueId,
                    text: `${name} (${module.UniqueId})`,
                })
            )
        );
        return moduleItem;
    }

    public purgeRecordData(): void {
        const deleteRequest: PurgeDataRequest = {
            TenantId: this.tenantService.selectedTenant.Value,
            ModuleIds: this.selectedModules,
        };
        this.purgeDataService
            .purgeModuleRecordData(deleteRequest)
            .subscribe((jobId) => (this.purgingJobId = jobId.toString()));
        this.purging = true;
    }

    public purgeHierarchyData(): void {
        this.purgeDataService
            .purgeHierarchyData(this.tenantService.selectedTenant.Value)
            .subscribe((jobId) => (this.purgingJobId = jobId.toString()));
        this.purging = true;
    }

    public ngOnDestroy(): void {
        this.subscriptions.forEach((sub) => this.unsubscribe(sub));
    }

    protected purgeRecordDataConfirm(): void {
        this.subscriptions.push(
            this.toast
                .sendMessage(
                    'Remove Record Data',
                    'Are you sure you want to remove this data?',
                    ToastMessageType.warning,
                    ToastButtons.YesNo,
                    0
                )
                .subscribe((choice: ToastResponse) => {
                    if (choice === ToastResponse.Yes) {
                        this.purgeLiveDataConfirm(() => this.purgeRecordData());
                    }
                })
        );
    }

    protected purgeHierarchyDataConfirm(): void {
        this.subscriptions.push(
            this.toast
                .sendMessage(
                    'Remove Hierarchy Data',
                    'Are you sure you want to remove this data?',
                    ToastMessageType.warning,
                    ToastButtons.YesNo,
                    0
                )
                .subscribe((choice: ToastResponse) => {
                    if (choice === ToastResponse.Yes) {
                        this.purgeLiveDataConfirm(() => this.purgeHierarchyData());
                    }
                })
        );
    }

    private getModules(): Observable<ModuleItem[]> {
        return this.tenantService
            .getAllTenantModules(this.tenantService.selectedTenant.Value)
            .pipe(
                map((modules) => {
                    return modules.map((module) => this.transformModule(module));
                }),
            );
    }

    private purgeLiveDataConfirm(callback: Function): void {
        this.subscriptions.push(
            this.tenantService
                .currentTenantStatus()
                .pipe(take(1))
                .subscribe((status) => {
                    if (status === 'LIVE') {
                        this.toast
                            .sendMessage(
                                'LIVE Tenant',
                                'YOU ARE ABOUT TO REMOVE DATA FROM A LIVE TENANT',
                                ToastMessageType.error,
                                ToastButtons.OkCancel,
                                0
                            )
                            .subscribe((response) =>
                                response === ToastResponse.Ok ? callback() : null
                            );
                    } else {
                        callback();
                    }
                })
        );
    }

    private unsubscribe(subscription: Subscription) {
        if (subscription !== null && subscription !== undefined) {
            subscription.unsubscribe();
        }
    }
}
